import React, { useState, useEffect, useContext, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { AuthContext, API } from '@/App';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { Home, LogOut, Users, Building2, FileSignature, ClipboardList, Menu, UserPlus } from 'lucide-react';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import NotificationBell from '@/components/NotificationBell';
import BackButton from '@/components/BackButton';
import ViewportMenuItems from '@/components/ViewportMenuItems';
import AdminStats from '@/components/admin/AdminStats';
import { UsersTable, PropertiesTable, LeasesTable, ApplicationsTable } from '@/components/admin/Tables';

const STATUS_CHIP_MAP = {
  active: 'bg-green-100 text-green-700',
  pending_landlord: 'bg-amber-100 text-amber-700',
  pending_payment: 'bg-blue-100 text-blue-700',
  cancelled: 'bg-red-100 text-red-700',
  completed: 'bg-muted text-foreground',
  approved: 'bg-green-100 text-green-700',
  rejected: 'bg-red-100 text-red-700',
  pending: 'bg-amber-100 text-amber-700',
  reviewed: 'bg-blue-100 text-blue-700',
  submitted: 'bg-blue-100 text-blue-700',
};

const statusChip = (status) => (
  <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_CHIP_MAP[status] || 'bg-muted text-foreground'}`}>
    {status?.replace(/_/g, ' ')}
  </span>
);

const INITIAL_ADD_USER = { email: '', name: '', password: '', phone: '', role: 'admin' };

export default function AdminDashboard() {
  const { user, logout } = useContext(AuthContext);
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [properties, setProperties] = useState([]);
  const [leases, setLeases] = useState([]);
  const [submissions, setSubmissions] = useState([]);
  const [showAddUserDialog, setShowAddUserDialog] = useState(false);
  const [addUserForm, setAddUserForm] = useState(INITIAL_ADD_USER);
  const [creatingUser, setCreatingUser] = useState(false);

  const fetchAll = useCallback(async () => {
    try {
      const [statsR, usersR, propsR, leasesR, subsR] = await Promise.all([
        axios.get(`${API}/admin/stats`),
        axios.get(`${API}/admin/users`),
        axios.get(`${API}/admin/properties`),
        axios.get(`${API}/admin/leases`),
        axios.get(`${API}/admin/form-submissions`),
      ]);
      setStats(statsR.data);
      setUsers(usersR.data);
      setProperties(propsR.data);
      setLeases(leasesR.data);
      setSubmissions(subsR.data);
    } catch (e) {
      toast.error('Failed to load admin data');
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const handleDeleteUser = useCallback(async (u) => {
    if (!window.confirm(`Delete ${u.name} (${u.email})? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/admin/users/${u.id}`);
      toast.success(`Deleted ${u.email}`);
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete user');
    }
  }, [fetchAll]);

  const handleCreateUser = useCallback(async () => {
    if (!addUserForm.email || !addUserForm.name || !addUserForm.password) {
      toast.error('Email, name and password are required');
      return;
    }
    if (addUserForm.password.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }
    setCreatingUser(true);
    try {
      const res = await axios.post(`${API}/admin/users`, {
        ...addUserForm, phone: addUserForm.phone || null,
      });
      toast.success(`${res.data.role === 'admin' ? 'Admin' : 'User'} created: ${res.data.email}`);
      setShowAddUserDialog(false);
      setAddUserForm(INITIAL_ADD_USER);
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create user');
    } finally {
      setCreatingUser(false);
    }
  }, [addUserForm, fetchAll]);

  const handleDeleteLease = useCallback(async (id, tenant, prop) => {
    if (!window.confirm(`Permanently delete the lease for "${prop}" (tenant: ${tenant})? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/admin/leases/${id}`);
      toast.success('Lease deleted');
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete lease');
    }
  }, [fetchAll]);

  const handleDeleteSubmission = useCallback(async (id, template, tenant) => {
    if (!window.confirm(`Permanently delete the "${template}" application from ${tenant}? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/admin/form-submissions/${id}`);
      toast.success('Application deleted');
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete application');
    }
  }, [fetchAll]);

  const handleViewProperty = useCallback((id) => navigate(`/property/${id}`), [navigate]);
  const handleViewLease = useCallback((id) => navigate(`/leases/${id}`), [navigate]);
  const setField = (k, v) => setAddUserForm(prev => ({ ...prev, [k]: v }));

  return (
    <div className="min-h-screen bg-background">
      <header className="glass-header bg-white/70 border-b border-border/40 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-3 sm:py-4 flex items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <BackButton fallback="/" />
            <div className="flex items-center gap-3" onClick={() => navigate('/')} style={{ cursor: 'pointer' }}>
              <Home className="w-7 h-7 text-primary" />
              <span className="text-2xl font-bold tracking-tight text-primary">PrimePocket</span>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <NotificationBell />
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button data-testid="admin-menu-trigger" variant="outline" size="sm" className="rounded-full gap-2">
                  <Menu className="w-4 h-4" />
                  <span className="hidden sm:inline">Menu</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-64" data-testid="admin-menu">
                <DropdownMenuLabel className="flex flex-col items-start">
                  <span className="font-semibold">{user?.name}</span>
                  <span className="text-xs text-muted-foreground font-normal">Admin</span>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem data-testid="menu-logout" onClick={logout} className="text-red-600 focus:text-red-700">
                  <LogOut className="w-4 h-4 mr-2" /> Logout
                </DropdownMenuItem>
                <ViewportMenuItems />
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-10">
        <div className="mb-10">
          <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold tracking-tight text-primary">Admin Dashboard</h1>
          <p className="text-muted-foreground mt-2">Platform-wide oversight and moderation.</p>
        </div>

        <AdminStats stats={stats} />

        <Tabs defaultValue="users" className="w-full">
          <TabsList className="bg-muted mb-6 flex-wrap h-auto">
            <TabsTrigger data-testid="users-tab" value="users" className="rounded-full px-6"><Users className="w-4 h-4 mr-2" />Users ({users.length})</TabsTrigger>
            <TabsTrigger data-testid="properties-tab" value="properties" className="rounded-full px-6"><Building2 className="w-4 h-4 mr-2" />Properties ({properties.length})</TabsTrigger>
            <TabsTrigger data-testid="leases-tab" value="leases" className="rounded-full px-6"><FileSignature className="w-4 h-4 mr-2" />Leases ({leases.length})</TabsTrigger>
            <TabsTrigger data-testid="applications-tab" value="applications" className="rounded-full px-6"><ClipboardList className="w-4 h-4 mr-2" />Applications ({submissions.length})</TabsTrigger>
          </TabsList>

          <TabsContent value="users">
            <div className="flex justify-end mb-4">
              <Dialog open={showAddUserDialog} onOpenChange={setShowAddUserDialog}>
                <DialogTrigger asChild>
                  <Button data-testid="add-user-btn" className="rounded-full gap-2 bg-primary hover:bg-primary/90">
                    <UserPlus className="w-4 h-4" /> Add User
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-md" data-testid="add-user-dialog">
                  <DialogHeader>
                    <DialogTitle>Create a new user</DialogTitle>
                    <DialogDescription>Choose a role and fill in the account details. Passwords must be at least 8 characters.</DialogDescription>
                  </DialogHeader>
                  <div className="space-y-4 mt-4">
                    <div>
                      <Label>Role</Label>
                      <Select value={addUserForm.role} onValueChange={(v) => setField('role', v)}>
                        <SelectTrigger data-testid="add-user-role" className="mt-2"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="admin">Admin</SelectItem>
                          <SelectItem value="landlord">Landlord</SelectItem>
                          <SelectItem value="tenant">Tenant</SelectItem>
                          <SelectItem value="employee">Employee</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Full Name</Label>
                      <Input data-testid="add-user-name" placeholder="Jane Doe" value={addUserForm.name} onChange={(e) => setField('name', e.target.value)} className="mt-2" />
                    </div>
                    <div>
                      <Label>Email</Label>
                      <Input data-testid="add-user-email" type="email" placeholder="admin2@primepocket.com" value={addUserForm.email} onChange={(e) => setField('email', e.target.value)} className="mt-2" />
                    </div>
                    <div>
                      <Label>Password</Label>
                      <Input data-testid="add-user-password" type="password" placeholder="At least 8 characters" value={addUserForm.password} onChange={(e) => setField('password', e.target.value)} className="mt-2" />
                    </div>
                    <div>
                      <Label>Phone (optional)</Label>
                      <Input data-testid="add-user-phone" placeholder="+1 555 123 4567" value={addUserForm.phone} onChange={(e) => setField('phone', e.target.value)} className="mt-2" />
                    </div>
                    <Button data-testid="add-user-submit" onClick={handleCreateUser} disabled={creatingUser} className="w-full rounded-full bg-primary hover:bg-primary/90">
                      {creatingUser ? 'Creating…' : 'Create user'}
                    </Button>
                  </div>
                </DialogContent>
              </Dialog>
            </div>
            <UsersTable users={users} currentUserId={user?.id} onDelete={handleDeleteUser} />
          </TabsContent>

          <TabsContent value="properties">
            <PropertiesTable properties={properties} onView={handleViewProperty} />
          </TabsContent>

          <TabsContent value="leases">
            <LeasesTable leases={leases} statusChip={statusChip} onView={handleViewLease} onDelete={handleDeleteLease} />
          </TabsContent>

          <TabsContent value="applications">
            <ApplicationsTable submissions={submissions} statusChip={statusChip} onDelete={handleDeleteSubmission} />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
